import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Upload } from "lucide-react";
import { toast } from "sonner";

const STATE_CLS = { new: "bg-emerald-50 text-emerald-800", unchanged: "bg-zinc-100 text-zinc-600", conflict: "bg-amber-100 text-amber-900", error: "bg-red-100 text-red-800" };
const STATE_LBL = { new: "Nuovo", unchanged: "Invariato", conflict: "Conflitto", error: "Errore" };
const errText = (e) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Errore"; };

export default function ImportPanel({ session: s, locked, onImported }) {
  const [prev, setPrev] = useState(null);
  const [overwrite, setOverwrite] = useState(false);
  const [skip, setSkip] = useState(false);
  const [busy, setBusy] = useState(false);
  const name = (aid) => { const p = s.participants.find((x) => x.athlete_id === aid); return p ? `${p.last_name} ${p.first_name}` : aid || "—"; };
  const tname = (tid) => s.tests.find((t) => t.id === tid)?.name || tid || "—";

  const download = async () => {
    const r = await api.get(`/test-sessions/${s.id}/template.xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `${s.code}_template.xlsx`; a.click(); URL.revokeObjectURL(url);
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post(`/test-sessions/${s.id}/import/preview`, fd); setPrev(data); setOverwrite(false); setSkip(false); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/test-sessions/${s.id}/import/${prev.id}/confirm`, { overwrite_conflicts: overwrite, skip_errors: skip });
      toast.success(`Import confermato · ${data.applied_groups} righe atleta/test aggiornate${data.skipped_conflicts ? ` · ${data.skipped_conflicts} celle in conflitto lasciate invariate` : ""}`); setPrev(null); onImported(data.session);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="import-panel">
      <div className="flex flex-wrap gap-2 items-center">
        <Button data-testid="import-template-btn" variant="outline" onClick={download}><Download className="w-4 h-4 mr-1" />Scarica template precompilato</Button>
        <label className={`inline-flex items-center gap-2 h-9 px-3 rounded-md border text-sm ${locked ? "opacity-50" : "cursor-pointer hover:bg-zinc-50"}`}>
          <Upload className="w-4 h-4" />{busy ? "Analisi…" : "Carica file compilato (.xlsx)"}
          <input data-testid="import-file-input" type="file" accept=".xlsx" className="hidden" disabled={locked || busy} onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
        </label>
        <span className="text-xs text-zinc-500">Il template contiene atleti presenti, test, versione, unità e tentativi della sessione. Nessun dato viene scritto prima della conferma.</span>
      </div>
      {prev && (
        <div className="bg-white border border-zinc-200 rounded-md p-4 space-y-3" data-testid="import-preview">
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(prev.counts).map(([k, v]) => <span key={k} data-testid={`import-count-${k}`} className={`px-2 py-1 rounded ${STATE_CLS[k]}`}>{STATE_LBL[k]}: {v}</span>)}
            <span className="text-zinc-500">File: {prev.filename}</span>
          </div>
          {prev.duplicate_of && <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="import-duplicate-file">Questo file è già stato importato in questa sessione.</div>}
          <div className="max-h-72 overflow-auto border border-zinc-100 rounded">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-zinc-50"><tr><th className="text-left px-2 py-1">Riga</th><th className="text-left px-2 py-1">Atleta</th><th className="text-left px-2 py-1">Test</th><th className="text-left px-2 py-1">Tent.</th><th className="text-left px-2 py-1">Esito</th><th className="text-left px-2 py-1">Dettaglio</th></tr></thead>
              <tbody>
                {prev.rows.filter((r) => r.state !== "unchanged").map((r) => (
                  <tr key={r.row} className="border-t border-zinc-100" data-testid={`import-row-${r.row}`}>
                    <td className="px-2 py-1 font-mono">{r.row}</td><td className="px-2 py-1">{name(r.athlete_id)}</td><td className="px-2 py-1">{tname(r.test_id)}</td><td className="px-2 py-1">{r.attempt ?? "—"}</td>
                    <td className="px-2 py-1"><span className={`px-1.5 rounded ${STATE_CLS[r.state]}`}>{STATE_LBL[r.state]}</span></td>
                    <td className="px-2 py-1">{r.errors.join("; ")}{r.previous ? `valore attuale: ${r.previous.valid ? r.previous.time : "annullata"}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {prev.counts.conflict > 0 && <label className="flex items-center gap-2 text-sm"><input data-testid="import-overwrite" type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />Sovrascrivi le {prev.counts.conflict} celle in conflitto (i valori precedenti restano nello storico). Senza spunta, solo le celle in conflitto mantengono il valore attuale; le altre celle nuove vengono importate.</label>}
          {prev.counts.error > 0 && <label className="flex items-center gap-2 text-sm"><input data-testid="import-skip-errors" type="checkbox" checked={skip} onChange={(e) => setSkip(e.target.checked)} />Escludi le {prev.counts.error} righe con errori e importa solo quelle valide</label>}
          <div className="flex gap-2">
            <Button data-testid="import-confirm-btn" disabled={busy || (prev.counts.error > 0 && !skip) || prev.counts.new + prev.counts.conflict === 0} onClick={confirm} className="bg-black hover:bg-zinc-800">Conferma import</Button>
            <Button data-testid="import-cancel-btn" variant="outline" onClick={() => setPrev(null)}>Annulla</Button>
          </div>
        </div>
      )}
    </div>
  );
}
